// src/layouts/Sidebar.js
import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useLocation, Link } from 'react-router-dom';
import { 
  FaHome, 
  FaClipboardList,
  FaTruck,
  FaFileInvoiceDollar,
  FaCalendarAlt, 
  FaCog, 
  FaTimes, 
  FaChevronDown,
  FaListAlt,
  FaTags,
  FaPhone,
  FaUsers,
  FaChartLine,
  FaRobot,
  FaSms,
  FaCalendarCheck,
  FaRoute,
  FaHardHat,
  FaMapMarkedAlt,
  FaBug
} from 'react-icons/fa';
import { useAuth } from '../modules/auth/AuthContext';

// const SidebarContainer = styled.aside`
//   background-color: ${props => props.theme.colors.surface};
//   width: 240px;
//   height: 100vh;
//   position: fixed;
//   top: 60px; /* Header height */
//   left: ${props => props.isOpen ? '0' : '-240px'};
//   transition: left 0.3s ease;
//   overflow-y: auto;
//   z-index: 999;

//   @media (min-width: 769px) {
//     left: 0;
//   }

//   /* Custom scrollbar */
//   &::-webkit-scrollbar {
//     width: 6px;
//   }

//   &::-webkit-scrollbar-track {
//     background: ${props => props.theme.colors.background};
//   }

//   &::-webkit-scrollbar-thumb {
//     background: ${props => props.theme.colors.primary};
//     border-radius: 3px;
//   }
// `;

const SidebarContainer = styled.aside`
  background-color: ${props => props.theme.colors.surface};
  width: 240px;
  position: fixed;
  top: 60px;
  left: 0;
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  overflow-y: auto;
  z-index: 999;
  transform: translateX(${props => props.isOpen ? '0' : '-100%'});

  /* On mobile browsers 100vh is the viewport with the toolbars HIDDEN, so
     calc(100vh - 60px) anchored at top:60px runs the bottom of this menu
     underneath the URL bar / tab strip — the last items become unreachable no
     matter how you scroll. dvh tracks the *visible* viewport and fixes it.
     vh first as the fallback for browsers without dvh (iOS < 15.4). */
  height: calc(100vh - 60px);
  height: calc(100dvh - 60px);

  /* Keep the scroll inside the menu: without this, reaching either end hands
     the gesture to the page behind, which reads as "the menu won't scroll". */
  overscroll-behavior: contain;

  padding-bottom: ${props => props.theme.spacing(3)};

  @media (max-width: 768px) {
    /* MobileNavigation is fixed to the bottom at z-index 1000 — ABOVE this
       drawer's 999 — so it sits on top of the menu and hides whatever scrolls
       under it. Reserve its height (~72px) plus the iPhone home indicator, so
       the last item can always be scrolled clear of it and tapped. */
    padding-bottom: calc(88px + env(safe-area-inset-bottom, 0px));
  }

  @media (min-width: 769px) {
    transform: translateX(0);
  }
`;

// const Overlay = styled.div`
//   position: fixed;
//   top: 60px;
//   left: 0;
//   right: 0;
//   bottom: 0;
//   background-color: rgba(0, 0, 0, 0.5);
//   z-index: 998;
//   opacity: ${props => props.isOpen ? 1 : 0};
//   visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
//   transition: opacity 0.3s ease, visibility 0.3s ease;

//   @media (min-width: 769px) {
//     display: none;
//   }
// `;

const Overlay = styled.div`
  position: fixed;
  top: 60px;
  left: 0;
  right: 0;
  bottom: 0;
  background-color: rgba(0, 0, 0, 0.5);
  z-index: 998;
  opacity: ${props => props.isOpen ? 1 : 0};
  visibility: ${props => props.isOpen ? 'visible' : 'hidden'};
  transition: opacity 0.3s ease, visibility 0.3s ease;
  backdrop-filter: blur(2px);

  @media (min-width: 769px) {
    display: none;
  }
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${props => props.theme.colors.textPrimary};
  font-size: 1.5rem;
  cursor: pointer;
  display: none;

  @media (max-width: 768px) {
    /* Sticky, not absolute: this lives inside the scrolling menu, and with ~56
       items an absolutely-positioned button scrolls out of reach the moment you
       start looking for anything near the bottom. */
    display: block;
    position: sticky;
    top: 0;
    z-index: 1;
    width: 100%;
    text-align: right;
    padding: ${props => props.theme.spacing(1)} ${props => props.theme.spacing(2)};
    background-color: ${props => props.theme.colors.surface};
  }
`;

const SectionHeader = styled.div`
  padding: ${props => props.theme.spacing(2)} ${props => props.theme.spacing(2)} ${props => props.theme.spacing(1)};
  margin-top: ${props => props.theme.spacing(1)};
  font-size: 0.7rem;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
  color: ${props => props.theme.colors.textSecondary};
  opacity: 0.65;
  border-top: 1px solid ${props => props.theme.colors.primary}1a;

  &:first-child {
    margin-top: 0;
    border-top: none;
  }
`;

const NavItem = styled.div`
  position: relative;
`;

/* Shared visual pattern for both button (branch) and link (leaf) rows.
   depth drives indentation; isActive adds a left accent bar + tint. */
const rowStyles = props => `
  display: flex;
  align-items: center;
  width: 100%;
  box-sizing: border-box;
  padding: ${props.theme.spacing(1.25)} ${props.theme.spacing(2)};
  padding-left: calc(${props.theme.spacing(2)} + ${props.depth || 0} * ${props.theme.spacing(2.5)});
  background: none;
  border: none;
  text-align: left;
  text-decoration: none;
  cursor: pointer;
  color: ${props.isActive || props.hasActiveChild ? props.theme.colors.primary : props.theme.colors.textPrimary};
  font-size: ${props.depth ? '0.9rem' : '1.02rem'};
  font-weight: ${props.isActive ? 600 : 400};
  transition: color 0.2s ease, background-color 0.2s ease, transform 0.2s cubic-bezier(0.4, 0, 0.2, 1);

  /* left accent bar for the active row */
  box-shadow: ${props.isActive ? `inset 3px 0 0 0 ${props.theme.colors.primary}` : 'none'};
  background-color: ${props.isActive ? `${props.theme.colors.primary}18` : 'transparent'};

  &:hover {
    background-color: ${props.theme.colors.primary}22;
    transform: translateX(3px);
  }

  svg {
    flex-shrink: 0;
    margin-right: ${props.theme.spacing(1.5)};
    font-size: ${props.depth ? '1rem' : '1.15rem'};
  }

  span {
    flex: 1;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
`;

const NavButton = styled.button`
  ${rowStyles}
`;

const NavLink = styled(Link)`
  ${rowStyles}
`;

const SubMenu = styled.div`
  overflow: hidden;
  transition: max-height 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  max-height: ${props => props.isOpen ? '1200px' : '0'};
  border-left: ${props => props.depth ? `2px solid ${props.theme.colors.primary}1f` : 'none'};
`;

const ChevronIcon = styled(FaChevronDown)`
  flex-shrink: 0;
  margin-left: ${props => props.theme.spacing(1)};
  font-size: 0.8rem;
  transform: ${props => props.isOpen ? 'rotate(180deg)' : 'rotate(0)'};
  transition: transform 0.3s ease;
`;

const navigationSections = [
  {
    section: 'Sales & CRM',
    items: [
      {
        id: 'dashboard',
        icon: <FaHome />,
        label: 'Dashboard',
        path: '/dashboard'
      },
      {
        id: 'leads',
        icon: <FaClipboardList />,
        label: 'Leads',
        path: '/leads',
        subItems: [
          { id: 'lead-view', label: 'All Leads', path: '/leads'},
          { id: 'lead-pipeline', label: 'Pipeline', path: '/leads/pipeline'},
          { id: 'lead-route-planner', label: 'Route Planner', path: '/leads/route-planner'},
          { id: 'lead-new', label: 'New Lead', path: '/leads/new'},
          { id: 'lead-status', label: 'Lead Status', path: '/leads/status' },
          { id: 'lead-origins', label: 'Lead Origins', path: '/leads/origins' }
        ]
      },
      {
        id: 'communications',
        icon: <FaPhone />,
        label: 'Communications',
        path: '/communications',
        subItems: [
          { id: 'comm-dashboard', label: 'Dashboard', path: '/communications/dashboard' },
          { id: 'comm-all', label: 'All Communications', path: '/communications/all' },
          { id: 'comm-unknown', label: 'Unknown Contacts', path: '/communications/placeholder-leads' }
        ]
      },
      {
        id: 'follow-ups',
        icon: <FaSms />,
        label: 'Follow-Ups',
        path: '/follow-ups',
        subItems: [
          { id: 'sms-templates', label: 'SMS Templates', path: '/follow-ups/templates' },
          { id: 'email-templates', label: 'Email Templates', path: '/follow-ups/email-templates' },
          { id: 'bulk-sms', label: 'Bulk SMS', path: '/follow-ups/bulk-sms' },
          { id: 'scheduled-sms', label: 'Scheduled Messages', path: '/follow-ups/scheduled' }
        ]
      },
      {
        id: 'call-scheduling',
        icon: <FaCalendarCheck />,
        label: 'Call Scheduling',
        path: '/bookings',
        subItems: [
          { id: 'bookings-all', label: 'All Bookings', path: '/bookings' },
          { id: 'bookings-availability', label: 'Availability', path: '/call-schedule/availability' }
        ]
      },
      {
        id: 'routes',
        icon: <FaRoute />,
        label: 'Routes',
        path: '/routes'
      }
    ]
  },
  {
    section: 'Operations',
    items: [
      {
        id: 'suppliers',
        icon: <FaTruck />,
        label: 'Suppliers',
        path: '/suppliers',
        subItems: [
          { id: 'supplier-list', label: 'All Suppliers', path: '/suppliers' },
          { id: 'supplier-new', label: 'Add Suppliers', path: '/suppliers/new' },
          { id: 'supplier-categories', label: 'Categories', path: '/suppliers/categories' },
          { id: 'products', label: 'Products', path: '/suppliers/products' }
        ]
      },
      {
        id: 'services',
        icon: <FaListAlt />,
        label: 'Services',
        path: '/services'
      },
      {
        id: 'cost-items',
        icon: <FaTags />,
        label: 'Cost Items',
        path: '/cost-items'
      },
      {
        id: 'estimates',
        icon: <FaFileInvoiceDollar />,
        label: 'Estimates',
        path: '/estimates',
        subItems: [
          { id: 'estimates-list', label: 'All Estimates', path: '/estimates' },
          { id: 'estimates-details', label: 'Estimate Details', path: '/estimates/detail' }
        ]
      },
      {
        id: 'jobs',
        icon: <FaCalendarAlt />,
        label: 'Jobs',
        path: '/jobs',
        subItems: [
          { id: 'jobs-dashboard', label: 'Dashboard', path: '/jobs' },
          { id: 'jobs-report',    label: 'Report',    path: '/jobs/report' }
        ]
      },
      {
        id: 'time-tracking',
        icon: <FaMapMarkedAlt />,
        label: 'Time Tracking',
        path: '/time-tracking',
        accessLevel: ['admin'],
        subItems: [
          { id: 'tt-report', label: 'Report', path: '/time-tracking' },
          { id: 'tt-payroll', label: 'Payroll', path: '/time-tracking/payroll' },
          { id: 'tt-employees', icon: <FaHardHat />, label: 'Employees', path: '/employees' },
          { id: 'tt-live', label: 'Live Map', path: '/time-tracking/live' },
          { id: 'tt-sites', label: 'Sites', path: '/time-tracking/sites' },
          { id: 'tt-standing', label: 'Standing Jobs', path: '/time-tracking/standing-jobs' },
          { id: 'tt-settings', label: 'Settings', path: '/time-tracking/settings' }
        ]
      }
    ]
  },
  {
    section: 'Finance',
    items: [
      {
        id: 'financial',
        icon: <FaChartLine />,
        label: 'Financial',
        path: '/financial',
        subItems: [
          { id: 'financial-dashboard', label: 'Overview', path: '/financial' },
          { id: 'financial-invoices', label: 'Invoices', path: '/financial/invoices' },
          { id: 'financial-expenses', label: 'Business Expenses', path: '/financial/expenses' }
        ]
      }
    ]
  },
  {
    section: 'Administration',
    items: [
      {
        id: 'settings',
        icon: <FaCog />,
        label: 'Settings',
        path: '/settings',
        subItems: [
          { id: 'settings-general', label: 'General Settings', path: '/settings' },
          {
            id: 'phone',
            icon: <FaPhone />,
            label: 'Phone',
            path: '/phone',
            subItems: [
              { id: 'phone-ring-cascade', label: 'Ring Cascade', path: '/phone/ring-cascade' }
            ]
          },
          {
            id: 'ai-agent',
            icon: <FaRobot />,
            label: 'AI Agent',
            path: '/ai-agent',
            subItems: [
              { id: 'ai-agent-config', label: 'Configuration', path: '/ai-agent' },
              { id: 'ai-agent-ivr', label: 'IVR Menu', path: '/ai-agent/ivr-menu' },
              { id: 'ai-agent-logs', label: 'Call Logs', path: '/ai-agent/call-logs' },
              { id: 'ai-agent-areas', label: 'Service Areas', path: '/ai-agent/service-areas' },
              { id: 'ai-estimate-prompts', label: 'Estimate AI Prompts', path: '/ai-agent/estimate-prompts' }
            ]
          }
        ]
      },
      {
        id: 'system-errors',
        icon: <FaBug />,
        label: 'System Errors',
        path: '/system/errors',
        accessLevel: ['admin'],
      },
      {
        id: 'users',
        icon: <FaUsers />,
        label: 'Users',
        path: '/users',
        accessLevel: ['admin'],
      }
    ]
  }
];

// Remove items the current role may not see (recurses into nested subItems).
const filterByRole = (items, role) =>
  items
    .filter((item) => !item.accessLevel || item.accessLevel.includes(role))
    .map((item) =>
      item.subItems
        ? { ...item, subItems: filterByRole(item.subItems, role) }
        : item
    );

const Sidebar = ({ isOpen, onClose }) => {
  const location = useLocation();
  const { user } = useAuth();
  const [expandedItems, setExpandedItems] = useState(new Set());

  const isPathActive = (path) =>
    location.pathname === path || location.pathname.startsWith(`${path}/`);

  // Sections with role-filtered items; drop any section left empty.
  const visibleSections = navigationSections
    .map((section) => ({
      ...section,
      items: filterByRole(section.items, user?.role),
    }))
    .filter((section) => section.items.length > 0);

  // The single best-matching leaf path (longest match wins) gets the full
  // active treatment, so /leads doesn't light up alongside /leads/pipeline.
  const activeLeafPath = (() => {
    let best = '';
    const walk = (items) => {
      items.forEach((item) => {
        if (item.subItems) {
          walk(item.subItems);
        } else if (isPathActive(item.path) && item.path.length > best.length) {
          best = item.path;
        }
      });
    };
    visibleSections.forEach((s) => walk(s.items));
    return best;
  })();

  // True when any leaf under this node is the active leaf (used to auto-expand
  // ancestors and to tint parent rows).
  const subtreeHasActive = (item) =>
    item.subItems
      ? item.subItems.some(subtreeHasActive)
      : item.path === activeLeafPath;

  // Auto-expand the branch(es) containing the active route whenever it changes.
  useEffect(() => {
    const ancestors = new Set();
    const walk = (items) => {
      items.forEach((item) => {
        if (item.subItems && item.subItems.some(subtreeHasActive)) {
          ancestors.add(item.id);
          walk(item.subItems);
        }
      });
    };
    visibleSections.forEach((s) => walk(s.items));
    setExpandedItems((prev) => new Set([...prev, ...ancestors]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname]);

  // Lock the page behind the drawer while it is open on mobile. Without this,
  // the body scrolls under the menu and a swipe that starts on the menu can be
  // handed to the page — which feels exactly like "the menu won't scroll".
  // Desktop is untouched: there the sidebar is always open and inline.
  useEffect(() => {
    if (typeof document === 'undefined') return undefined;
    const isMobile = window.innerWidth <= 768;
    if (!isOpen || !isMobile) return undefined;

    const previous = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = previous; };
  }, [isOpen]);

  const toggleSubMenu = (itemId) => {
    setExpandedItems((prev) => {
      const next = new Set(prev);
      next.has(itemId) ? next.delete(itemId) : next.add(itemId);
      return next;
    });
  };

  const handleLinkClick = () => {
    if (window.innerWidth <= 768) {
      onClose();
    }
  };

  const renderNode = (item, depth) => {
    if (item.subItems) {
      const expanded = expandedItems.has(item.id);
      return (
        <NavItem key={item.id}>
          <NavButton
            depth={depth}
            hasActiveChild={subtreeHasActive(item)}
            onClick={() => toggleSubMenu(item.id)}
          >
            {item.icon}
            <span>{item.label}</span>
            <ChevronIcon isOpen={expanded} />
          </NavButton>
          <SubMenu isOpen={expanded} depth={depth + 1}>
            {item.subItems.map((sub) => renderNode(sub, depth + 1))}
          </SubMenu>
        </NavItem>
      );
    }

    return (
      <NavItem key={item.id}>
        <NavLink
          to={item.path}
          depth={depth}
          isActive={item.path === activeLeafPath}
          onClick={handleLinkClick}
        >
          {item.icon}
          <span>{item.label}</span>
        </NavLink>
      </NavItem>
    );
  };

  return (
    <>
      <Overlay isOpen={isOpen} onClick={onClose} />
      <SidebarContainer isOpen={isOpen}>
        <CloseButton onClick={onClose}>
          <FaTimes />
        </CloseButton>

        {visibleSections.map((section) => (
          <div key={section.section}>
            <SectionHeader aria-hidden="true">{section.section}</SectionHeader>
            {section.items.map((item) => renderNode(item, 0))}
          </div>
        ))}
      </SidebarContainer>
    </>
  );
};

export default Sidebar;

// // src/layouts/Sidebar.js
// import React from 'react';
// import styled from 'styled-components';
// import { useNavigate } from 'react-router-dom';
// import { FaTimes, FaHome, FaClipboardList, FaTruck, FaBoxes, FaFileInvoiceDollar, FaCalendarAlt, FaCog } from 'react-icons/fa';

// const SidebarContainer = styled.div`
//   background-color: ${(props) => props.theme.colors.surface};
//   width: 240px;
//   height: 100%;
//   position: fixed;
//   top: 0;
//   left: ${(props) => (props.isOpen ? '0' : '-240px')};
//   transition: left 0.3s ease-in-out;
//   z-index: 1000;
//   overflow-y: auto;
//   padding-top: 60px;

//   @media (max-width: 768px) {
//     width: 100%;
//     left: ${(props) => (props.isOpen ? '0' : '-100%')};
//   }
// `;

// const CloseButton = styled.button`
//   position: absolute;
//   top: 10px;
//   right: 10px;
//   background: none;
//   border: none;
//   color: ${(props) => props.theme.colors.textPrimary};
//   font-size: 1.5rem;
//   cursor: pointer;
// `;

// const SidebarItem = styled.div`
//   display: flex;
//   align-items: center;
//   padding: 15px 20px;
//   color: ${(props) => props.theme.colors.textPrimary};
//   cursor: pointer;
//   transition: background-color 0.2s;

//   &:hover {
//     background-color: ${(props) => props.theme.colors.primary};
//     color: white;
//   }

//   svg {
//     margin-right: 10px;
//     font-size: 1.2rem;
//   }
// `;

// const Sidebar = ({ isOpen, onClose }) => {
//   const navigate = useNavigate();

//   const handleNavigation = (path) => {
//     navigate(path);
//     onClose();
//   };

//   return (
//     <SidebarContainer isOpen={isOpen}>
//       <CloseButton onClick={onClose}>
//         <FaTimes />
//       </CloseButton>
//       <SidebarItem onClick={() => handleNavigation('/dashboard')}>
//         <FaHome />
//         Dashboard
//       </SidebarItem>
//       <SidebarItem onClick={() => handleNavigation('/leads')}>
//         <FaClipboardList />
//         Leads
//       </SidebarItem>
//       <SidebarItem onClick={() => handleNavigation('/suppliers')}>
//         <FaTruck />
//         Suppliers
//       </SidebarItem>
//       <SidebarItem onClick={() => handleNavigation('/products')}>
//         <FaBoxes />
//         Products
//       </SidebarItem>
//       <SidebarItem onClick={() => handleNavigation('/estimates')}>
//         <FaFileInvoiceDollar />
//         Estimates
//       </SidebarItem>
//       <SidebarItem onClick={() => handleNavigation('/jobs')}>
//         <FaCalendarAlt />
//         Jobs
//       </SidebarItem>
//       <SidebarItem onClick={() => handleNavigation('/settings')}>
//         <FaCog />
//         Settings
//       </SidebarItem>
//     </SidebarContainer>
//   );
// };

// export default Sidebar;

// // // src/layouts/Sidebar.js
// // import React, { useState } from 'react';
// // import styled from 'styled-components';
// // import { FaHome, FaUser, FaCog, FaBars } from 'react-icons/fa';
// // import { useNavigate } from 'react-router-dom';

// // const SidebarContainer = styled.div`
// //   background-color: ${(props) => props.theme.colors.surface};
// //   width: ${(props) => (props.collapsed ? '60px' : '240px')};  // Width toggles between collapsed and expanded
// //   transition: width 0.3s;
// //   height: 100%;
// //   display: flex;
// //   flex-direction: column;
// //   align-items: ${(props) => (props.collapsed ? 'center' : 'flex-start')};
// //   padding: 10px;
// // `;

// // const SidebarItem = styled.div`
// //   display: flex;
// //   align-items: center;
// //   padding: 10px;
// //   cursor: pointer;
// //   color: ${(props) => props.theme.colors.textPrimary};
// //   margin-bottom: 20px;

// //   &:hover {
// //     background-color: ${(props) => props.theme.colors.primary};
// //     color: white;
// //   }

// //   svg {
// //     margin-right: ${(props) => (props.$collapsed ? '0' : '10px')};
// //     font-size: 1.5rem;
// //   }

// //   span {
// //     display: ${(props) => (props.$collapsed ? 'none' : 'inline')};
// //   }
// // `;

// // const ToggleButton = styled.div`
// //   margin-bottom: 30px;
// //   cursor: pointer;
// // `;

// // const Sidebar = () => {
// //   const [collapsed, setCollapsed] = useState(false);
// //   const navigate = useNavigate();

// //   return (
// //     <SidebarContainer collapsed={collapsed}>
// //       <ToggleButton onClick={() => setCollapsed(!collapsed)}>
// //         <FaBars />
// //       </ToggleButton>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/dashboard')}>
// //         <FaHome />
// //         <span>Dashboard</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/profile')}>
// //         <FaUser />
// //         <span>Profile</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/settings')}>
// //         <FaCog />
// //         <span>Settings</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/suppliers')}>
// //         <FaCog />
// //         <span>Suppliers</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/supplier-categories')}>
// //         <FaCog />
// //         <span>Supplier Categories</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/products')}>
// //         <FaCog />
// //         <span>Supplier Products</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/leads/')}>
// //         <FaCog />
// //         <span>Leads</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/leads/status')}>
// //         <FaCog />
// //         <span>Leads Status</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/leads/origins')}>
// //         <FaCog />
// //         <span>Leads Origins</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/services')}>
// //         <FaCog />
// //         <span>Services</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/estimates')}>
// //         <FaCog />
// //         <span>Estimates</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/estimates-detail')}>
// //         <FaCog />
// //         <span>Estimates Detail</span>
// //       </SidebarItem>
// //       <SidebarItem collapsed={collapsed} onClick={() => navigate('/jobs')}>
// //         <FaCog />
// //         <span>Schedule Jobs</span>
// //       </SidebarItem>
// //     </SidebarContainer>
// //   );
// // };

// // export default Sidebar;
